/* Progressive presentation only: the theme still owns variants, stock and cart requests. */
if (!customElements.get('uoozee-mobile-product')) {
  customElements.define('uoozee-mobile-product', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.mobile = matchMedia('(max-width: 767px)');
      this.sizingOrigins = new WeakMap();
      this.headingOrigins = new Map();
      this.gallery = this.querySelector('.product-images');
      this.mediaContainer = this.querySelector('.product-image-container');
      this.sourceButton = this.querySelector('.product-information button[name="add"]');
      this.bar = document.createElement('div');
      this.bar.className = 'uoozee-product-bar';
      this.barButton = document.createElement('button');
      this.barButton.type = 'button';
      this.bar.append(this.barButton);
      document.body.append(this.bar);
      const options = { signal: this.controller.signal };
      this.barButton.addEventListener('click', () => {
        // A real click on the original submit button preserves validation, quantity,
        // gift-card fields, app listeners and the theme's AJAX cart/error handling.
        this.sourceButton?.scrollIntoView({ block: 'center', behavior: 'instant' });
        this.sourceButton?.click();
      }, options);
      this.refresh = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      };
      this.observer = new MutationObserver(this.refresh);
      this.observer.observe(this, { childList: true, subtree: true, attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled', 'style'] });
      this.gallery?.addEventListener('scroll', this.refresh, { ...options, passive: true });
      if (this.gallery) {
        this.resizeObserver = new ResizeObserver(() => {
          this.gallery.flkty?.resize();
          this.refresh();
        });
        this.resizeObserver.observe(this.gallery);
      }
      this.mobile.addEventListener('change', this.refresh, options);
      this.setupProductCode();
      this.setupShare(options);
      this.update();
    }

    setupProductCode() {
      try {
        const codes = JSON.parse(this.querySelector('[data-product-skus]')?.textContent || '[]')
          .map(sku => /^CY-!(\d+)(?:-|$)/.exec(sku || '')?.[1] || '');
        const label = this.querySelector('.uoozee-product-code');
        if (label && codes[0] && codes.every(code => code === codes[0])) {
          label.querySelector('span').textContent = codes[0];
          label.hidden = false;
        }
      } catch (_) { /* Unknown SKU formats are never guessed. */ }
    }

    setupShare(options) {
      const button = this.querySelector('.uoozee-product-share');
      if (!button || !navigator.share) return;
      button.hidden = false;
      button.addEventListener('click', async () => {
        try {
          const url = new URL(location.href);
          url.searchParams.delete('preview_theme_id');
          url.searchParams.delete('pb');
          await navigator.share({ title: this.querySelector('h1')?.textContent, url: url.href });
        } catch (_) { /* User cancelling the share sheet is normal. */ }
      }, options);
    }

    positionSizingGuides(inline = this.mobile.matches) {
      this.querySelectorAll('.product-information .product-form__input').forEach(field => {
        const label = field.querySelector(':scope > .form__label');
        const opener = field.querySelector('.product-popup-modal__opener[data-modal]');
        if (!label || !opener) return;
        if (inline && opener.parentElement !== label) {
          const marker = document.createComment('Sizing guide original position');
          opener.before(marker);
          this.sizingOrigins.set(opener, marker);
          opener.classList.add('uoozee-inline-size-guide');
          label.append(opener);
        } else if (!inline && this.sizingOrigins.has(opener)) {
          const marker = this.sizingOrigins.get(opener);
          if (marker.parentNode) { marker.before(opener); marker.remove(); }
          else field.querySelector(':scope > .product-form__input-wrapper')?.append(opener);
          opener.classList.remove('uoozee-inline-size-guide');
          this.sizingOrigins.delete(opener);
        }
      });
    }

    positionProductHeading(aboveGallery = this.mobile.matches) {
      if (!aboveGallery) {
        this.headingOrigins.forEach((marker, node) => {
          if (marker.parentNode) marker.replaceWith(node);
        });
        this.headingOrigins.clear();
        this.heading?.remove();
        this.heading = null;
        return;
      }
      const title = this.querySelector('.product-information .product-title');
      const grid = this.querySelector('.product-grid-container');
      if (!title || !grid) return;
      if (!this.heading) {
        this.heading = document.createElement('div');
        this.heading.className = 'product-title-container uoozee-product-heading';
        grid.prepend(this.heading);
      }
      // Move the real title/share nodes, keeping one H1 and existing listeners.
      // Price and reviews stay in the original information block below the gallery.
      [title, this.querySelector('.product-information .uoozee-product-share')].forEach(node => {
        if (!node) return;
        const marker = document.createComment('Product heading original position');
        node.before(marker);
        this.headingOrigins.set(node, marker);
        this.heading.append(node);
      });
    }

    update() {
      if (!this.isConnected) return;
      this.positionProductHeading();
      this.positionSizingGuides();
      this.sourceButton = this.querySelector('.product-information button[name="add"]');
      const active = this.mobile.matches && !!this.sourceButton;
      this.bar.hidden = !active;
      document.body.classList.toggle('uoozee-product-bar-active', active);
      if (this.sourceButton) {
        const text = this.sourceButton.querySelector('.single-add-to-cart-button--text')?.textContent.trim();
        if (this.barButton.textContent !== text) this.barButton.textContent = text;
        this.barButton.disabled = this.sourceButton.disabled || this.sourceButton.classList.contains('loading');
      }
      const flickity = this.gallery?.flkty;
      if (flickity && flickity !== this.flickity) {
        this.flickity?.off('select', this.refresh);
        this.flickity = flickity;
        flickity.on('select', this.refresh);
      }
      const slides = flickity?.cells?.map(cell => cell.element) ||
        [...(this.gallery?.querySelectorAll('.product-images__slide') || [])].filter(slide => slide.offsetWidth);
      let index = flickity?.selectedIndex ?? 0;
      if (!flickity && slides.length) {
        const center = this.gallery.getBoundingClientRect().left + this.gallery.clientWidth / 2;
        let distance = Infinity;
        slides.forEach((slide, i) => {
          const rect = slide.getBoundingClientRect();
          const delta = Math.abs(rect.left + rect.width / 2 - center);
          if (delta < distance) { index = i; distance = delta; }
        });
      }
      if (this.mobile.matches) {
        // Wrapped neighbours are visible on both sides, including the first/last slide.
        [slides[index], slides[(index + 1) % slides.length],
          slides[(index - 1 + slides.length) % slides.length]].forEach(slide => {
          slide?.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
        });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.positionProductHeading(false);
      this.positionSizingGuides(false);
      this.resizeObserver?.disconnect();
      this.flickity?.off('select', this.refresh);
      cancelAnimationFrame(this.frame);
      this.bar?.remove();
      document.body.classList.remove('uoozee-product-bar-active');
    }
  });
}
